import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import ToolShell from "./ToolShell";
import SavePrompt from "./SavePrompt";
import { Panel, TextField, SelectField } from "./ToolUI";
import { saveResult, loadDraft, saveDraft } from "./toolkit";

const EMPTY = {
  number: "", title: "", owner: "", opened: new Date().toISOString().slice(0, 10), severity: "Major",
  d1: "", what: "", where: "", when: "", howMuch: "", d3: "",
  whys: ["", "", "", "", ""], rootCause: "",
  actions: [{ action: "", owner: "", due: "" }], d6: "", d7: "", d8: "",
};

const SECTIONS = [
  { id: "d0", label: "D0 · Header", done: (c) => c.title && c.owner },
  { id: "d1", label: "D1 · Team", done: (c) => c.d1 },
  { id: "d2", label: "D2 · Problem", done: (c) => c.what && c.where },
  { id: "d3", label: "D3 · Containment", done: (c) => c.d3 },
  { id: "d4", label: "D4 · Root cause", done: (c) => c.whys.filter(Boolean).length >= 3 && c.rootCause },
  { id: "d5", label: "D5 · Actions", done: (c) => c.actions.some((a) => a.action && a.owner) },
  { id: "d6", label: "D6 · Verify", done: (c) => c.d6 },
  { id: "d7", label: "D7 · Prevent", done: (c) => c.d7 },
  { id: "d8", label: "D8 · Close", done: (c) => c.d8 },
];

const Whys = ({ c, up }) => (
  <div className="space-y-3">
    {c.whys.map((w, i) => (
      <div key={i} className="grid grid-cols-[auto_1fr] gap-3 items-start">
        <div className="mt-3 text-xs font-mono-plex text-primary w-14">WHY {i + 1}</div>
        <TextField label={i === 0 ? `Why did "${c.what || "the problem"}" happen?` : "Why did that happen?"} value={w}
          onChange={(v) => up({ whys: c.whys.map((x, j) => (j === i ? v : x)) })} testid={`capa-why-${i}`} />
      </div>
    ))}
    <TextField label="Verified root cause" value={c.rootCause} onChange={(v) => up({ rootCause: v })} testid="capa-root-cause" multiline rows={2} />
  </div>
);

const Actions = ({ c, up }) => {
  const set = (i, k, v) => up({ actions: c.actions.map((a, j) => (j === i ? { ...a, [k]: v } : a)) });
  return (
    <div className="space-y-3">
      {c.actions.map((a, i) => (
        <div key={i} className="grid sm:grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end">
          <TextField label={`Action ${i + 1}`} value={a.action} onChange={(v) => set(i, "action", v)} testid={`capa-action-${i}`} />
          <TextField label="Owner" value={a.owner} onChange={(v) => set(i, "owner", v)} testid={`capa-action-owner-${i}`} />
          <TextField type="date" label="Due" value={a.due} onChange={(v) => set(i, "due", v)} testid={`capa-action-due-${i}`} />
          <button type="button" onClick={() => up({ actions: c.actions.filter((_, j) => j !== i) })} disabled={c.actions.length === 1}
            className="h-11 px-3 border border-border rounded-sm text-muted-foreground hover:text-red-400 disabled:opacity-30 no-print" data-testid={`capa-action-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => up({ actions: [...c.actions, { action: "", owner: "", due: "" }] })}
        className="text-sm text-primary inline-flex items-center hover:underline no-print" data-testid="capa-add-action"><Plus className="w-4 h-4 mr-1" />Add action</button>
    </div>
  );
};

export default function CAPABuilder() {
  const [c, setC] = useState(() => loadDraft("capa", EMPTY));
  const up = (patch) => setC((s) => ({ ...s, ...patch }));
  const t = (k) => (v) => up({ [k]: v });
  const doneCount = useMemo(() => SECTIONS.filter((s) => s.done(c)).length, [c]);
  const progress = Math.round((doneCount / SECTIONS.length) * 100);
  const complete = doneCount === SECTIONS.length;

  useEffect(() => { saveDraft("capa", c); }, [c]);
  useEffect(() => { if (complete) saveResult("capa", { score: progress, title: c.title }); }, [complete, progress, c.title]);

  const share = complete ? {
    slug: "capa-builder", tool: "8D CAPA Report", scoreText: "8D ✓", buttonLabel: "Share my CAPA milestone",
    label: `Root cause found & ${c.actions.filter((a) => a.action).length} corrective action(s) assigned`,
    bar: { value: 100, target: 100, targetLabel: "All 8 disciplines" },
    tagline: "Closing the loop on quality with RuleAI",
    caption: "Just closed out a full 8D / 5-Why CAPA — root cause verified, actions assigned. Built free with RuleAI Systems.",
  } : null;

  return (
    <ToolShell toolKey="capa" overline="Free tool · Quality" title="CAPA Builder"
      intro="A guided 8D corrective & preventive action report with a built-in 5-Why. Fill it in on the floor, then print or save as PDF for your QMS. Your draft saves automatically in this browser."
      share={share} results={{ progress, ...c }}
      savePrompt={complete && <SavePrompt toolKey="capa" what="CAPA" />}>
      <div className="sticky top-14 z-30 -mx-5 lg:mx-0 px-5 py-3 bg-background/90 backdrop-blur border-b lg:border border-border lg:rounded-sm no-print" data-testid="capa-progress">
        <div className="flex justify-between text-xs font-mono-plex"><span>{doneCount}/{SECTIONS.length} disciplines complete</span><span data-testid="capa-progress-value">{progress}%</span></div>
        <div className="h-1.5 bg-muted rounded-sm mt-2 overflow-hidden"><div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${progress}%` }} /></div>
        <div className="flex flex-wrap gap-1.5 mt-2">{SECTIONS.map((s) => <span key={s.id} className={`text-[10px] font-mono-plex px-1.5 py-0.5 rounded-sm border ${s.done(c) ? "border-emerald-500/40 text-emerald-400" : "border-border text-muted-foreground"}`}>{s.label}</span>)}</div>
      </div>
      <Panel overline="D0" title="Header" testid="capa-d0">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <TextField label="CAPA #" value={c.number} onChange={t("number")} placeholder="CAPA-2026-014" testid="capa-number" />
          <div className="lg:col-span-2"><TextField label="Title" value={c.title} onChange={t("title")} placeholder="Short-shot on part 4471" testid="capa-title" /></div>
          <TextField label="Owner" value={c.owner} onChange={t("owner")} testid="capa-owner" />
          <SelectField label="Severity" value={c.severity} onChange={t("severity")} options={["Critical", "Major", "Minor"]} testid="capa-severity" />
        </div>
      </Panel>
      <Panel overline="D1" title="Form the team" testid="capa-d1">
        <TextField label="Team members & roles" value={c.d1} onChange={t("d1")} multiline testid="capa-d1-input" placeholder="Quality engineer (lead), process tech, operator, maintenance…" />
      </Panel>
      <Panel overline="D2" title="Describe the problem" testid="capa-d2">
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="What happened?" value={c.what} onChange={t("what")} multiline rows={2} testid="capa-what" />
          <TextField label="Where? (line, machine, customer)" value={c.where} onChange={t("where")} multiline rows={2} testid="capa-where" />
          <TextField label="When was it first detected?" value={c.when} onChange={t("when")} testid="capa-when" />
          <TextField label="How much / how many affected?" value={c.howMuch} onChange={t("howMuch")} testid="capa-how-much" />
        </div>
      </Panel>
      <Panel overline="D3" title="Interim containment" testid="capa-d3">
        <TextField label="How are customers protected right now?" value={c.d3} onChange={t("d3")} multiline testid="capa-d3-input" placeholder="100% sort of WIP and finished goods, quarantine lot #…" />
      </Panel>
      <Panel overline="D4" title="Root cause — 5 Whys" testid="capa-d4"><Whys c={c} up={up} /></Panel>
      <Panel overline="D5" title="Permanent corrective actions" testid="capa-d5"><Actions c={c} up={up} /></Panel>
      <Panel overline="D6" title="Implement & verify effectiveness" testid="capa-d6">
        <TextField label="Evidence the fix works" value={c.d6} onChange={t("d6")} multiline testid="capa-d6-input" placeholder="30 days / 3 lots with zero recurrence, Cpk 1.67…" />
      </Panel>
      <Panel overline="D7" title="Prevent recurrence" testid="capa-d7">
        <TextField label="Systemic changes (SOPs, PFMEA, control plan, training)" value={c.d7} onChange={t("d7")} multiline testid="capa-d7-input" />
      </Panel>
      <Panel overline="D8" title="Close & recognize the team" testid="capa-d8">
        <TextField label="Closure notes & sign-off" value={c.d8} onChange={t("d8")} multiline testid="capa-d8-input" />
      </Panel>
      <button type="button" onClick={() => setC(EMPTY)} className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-foreground no-print" data-testid="capa-reset">Clear form</button>
    </ToolShell>
  );
}
